import { DatePipe } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { lastValueFrom } from "rxjs";
import { Backup, Organisation } from "../../common/organisation";
import { CURRENT_STATE_PROJECT_KEY } from "../../common/linqset-model";
import { UserService } from "../../user/user.service";
import { USER_ROLE } from "../../common/user";
import { BackupService } from "./backup.service";
import { Column, TableComponent } from "../../ui-components/table/table-component";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../../ui-components/dialogs/confirm-dialog.component";

// what the table shows for a backup; the organisation backup covers every model
interface BackupRow {
    _key:string
    isOrganisation:boolean
    projectKey:string
    modelKey:string
    workspace:string
    modelName:string
    date:Date
    description:string
    user:string
    nodeCount:number
    edgeCount:number
    canRestore:boolean
    canDelete:boolean
}

@Component({
    selector: 'app-backups-table',
    templateUrl: './backups-table.html',
    styleUrls: ['./backups-table.css'],
    standalone: true,
    imports: [TableComponent, DatePipe]
})
export class BackupsTableComponent implements OnChanges {
    @Input() organisation:Organisation

    // the organisation backup is restored by the page, which checks it first
    @Output() restoreOrganisationBackupEvent = new EventEmitter<void>()
    @Output() deleteBackupEvent = new EventEmitter<string>()

    backupData:BackupRow[] = []
    columns:Column[] = []
    modelBackupCount = 0

    constructor(private userService:UserService, private backupService:BackupService, private dialog:MatDialog) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organisation'] && this.organisation) this.populateData()
    }

    // the server only lets organisation admins restore or delete backups
    private isAdmin() {
        return this.userService.isSysAdmin() || this.organisation.role === USER_ROLE.ADMIN
    }

    private workspaceName(projectKey:string) {
        if (!projectKey || projectKey === CURRENT_STATE_PROJECT_KEY) return "Current state"
        return this.organisation.projects.find(p => p._key === projectKey)?.name || "Deleted project"
    }

    private row(backup:Backup, modelKey:string, modelName:string, workspace:string, isOrganisation:boolean):BackupRow {
        const admin = this.isAdmin()
        return {
            _key: backup._key,
            isOrganisation,
            projectKey: backup.projectKey,
            modelKey,
            workspace,
            modelName,
            date: backup.date,
            description: backup.description,
            user: backup.user || '',
            nodeCount: backup.nodeCount,
            edgeCount: backup.edgeCount,
            canRestore: admin,
            canDelete: admin,
        }
    }

    populateData() {
        const modelRows = this.organisation.models
            .filter(m => m.backups?.length)
            .flatMap(m => m.backups.map(b => this.row(b, m._key, m.deletedDate ? `${m.name} (archived)` : m.name, this.workspaceName(b.projectKey), false)))
            // newest first
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        this.modelBackupCount = modelRows.length

        const {backup} = this.organisation
        this.backupData = backup ? [this.row(backup, this.organisation.model?._key, 'All models', 'Whole organisation', true), ...modelRows] : modelRows

        this.columns = [
            new Column('workspace', 'Covers', 'string'),
            new Column('modelName', 'Model', 'string'),
            new Column('date', 'Created', 'datetime'),
            new Column('description', 'Description', 'string'),
            new Column('user', 'By', 'string'),
            new Column('nodeCount', 'Nodes', 'number'),
            new Column('edgeCount', 'Edges', 'number'),
        ]
        if (this.isAdmin()) {
            this.columns.push(new Column('canRestore', 'Restore', 'icon-button', 'Replace the current data with this backup', 'accent', 'restore'))
            this.columns.push(new Column('canDelete', 'Delete', 'icon-button', 'Delete this backup permanently', 'warn', 'delete'))
        }
    }

    async handleRowEvent(rowEvent:{row:BackupRow, column?:Column}) {
        const row = rowEvent.row
        if (!row || !rowEvent.column || !this.isAdmin()) return
        if (rowEvent.column.name === "canDelete") this.deleteBackup(row)
        else if (rowEvent.column.name === "canRestore") this.restoreBackup(row)
    }

    restoreBackup(row:BackupRow) {
        if (row.isOrganisation) {
            this.restoreOrganisationBackupEvent.emit()
            return
        }
        const model = this.organisation.models.find(m => m._key === row.modelKey)
        const project = this.organisation.projects.find(p => p._key === row.projectKey)
        if (model) this.backupService.confirmRestoreBackup(this.organisation, model, project, row._key)
    }

    async deleteBackup(row:BackupRow) {
        const what = row.isOrganisation ? 'the organisation backup' : `the backup of ${row.modelName} (${row.workspace})`
        const message = `Permanently delete ${what} from ${new Date(row.date).toLocaleString()}? This can't be undone.`
        const dialogData = new ConfirmDialogModel("Delete backup", [message], undefined, false, "Delete", "Cancel")
        const result = await lastValueFrom(this.dialog.open(ConfirmDialogComponent, { maxWidth: "450px", data: dialogData }).afterClosed())
        if (result?.button !== "Delete") return

        if (row.isOrganisation) {
            await this.backupService.deleteOrganisationBackup(this.organisation)
            this.organisation.backup = undefined
        }else {
            await this.backupService.deleteBackup(this.organisation, row.modelKey, row._key, row.projectKey)
            const model = this.organisation.models.find(m => m._key === row.modelKey)
            if (model) model.backups = model.backups.filter(a => a._key !== row._key)
        }
        this.populateData()
        this.deleteBackupEvent.emit(row._key)
    }
}
